import { useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { initialOf } from '../utilities/user';
import InfoTip from '../reusable/InfoTip/InfoTip';
/* The same rows the sidebar draws under its own "Get started" heading. Read
   from `nav.js` rather than copied: this list used to be a local stand-in
   because that group was commented out, and a copy is exactly how the phone's
   menu and the sidebar drift apart. `dock: false` on the group is what sends
   them here instead of into the dock — the dock is glyphs, this is words. */
import { GET_STARTED } from './nav';
import { t } from '../i18n/i18n';
import './AccountSheet.scss';

const icon = (name) => ({ '--icon': `url(/icons/${name}.svg)` });

/**
 * The phone's account menu.
 *
 * Below 900px the sidebar goes, and its account card goes with it — which left
 * Profile, Public Page, Help centre and Log out with nowhere to be reached
 * from. This carries those, and takes over the two top-bar dropdowns that had
 * the least room to open on a narrow row: the balance breakdown and the
 * notification list. Those two are retired on mobile (layout.scss) rather than
 * drawn twice, which is what keeps the row to brand · search · account ·
 * avatar.
 *
 * Anchored and sized the way the dropdowns it replaces already are on mobile —
 * fixed under the top bar, inset 12px, scrolling inside a viewport-bound
 * height — so it lands where a trader's thumb already expects a panel. Depth
 * is the raised surface plus a shadow: the Style Guide bans the 1px border the
 * older dropdowns still carry.
 */
export default function AccountSheet({
  open,
  onClose,
  name,
  email,
  balances = [],
  notifications = [],
  hasUnread,
  onMarkRead,
  onPublicPage,
  onSignOut,
}) {
  const [notesOpen, setNotesOpen] = useState(false);

  // Reopening starts at the menu rather than wherever it was last left.
  useEffect(() => {
    if (!open) setNotesOpen(false);
  }, [open]);

  // Escape closes it, which a pointer-only scrim does not offer a keyboard.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  return (
    <>
      <div
        className={`acct-sheet__scrim${open ? ' is-open' : ''}`}
        onClick={onClose}
        aria-hidden="true"
      />

      <div
        className={`acct-sheet${open ? ' is-open' : ''}`}
        role="menu"
        aria-label={t('Account')}
        inert={!open ? '' : undefined}
      >
        <header className="acct-sheet__id">
          <span className="acct-sheet__avatar">{initialOf(name)}</span>
          <span className="acct-sheet__who">
            <span className="acct-sheet__name">{name}</span>
            <span className="acct-sheet__email">{email}</span>
          </span>
        </header>

        {balances.length > 0 && (
          <dl className="acct-sheet__balances">
            {balances.map((row) => (
              <div
                className={`acct-sheet__balance${row.rule ? ' is-ruled' : ''}`}
                key={row.label}
              >
                <dt className="acct-sheet__balance-label">
                  {t(row.label)}
                  {/* What the figure means, from the dashboard response. */}
                  {row.info && (
                    <InfoTip
                      className="acct-sheet__balance-info"
                      label={t('What {{label}} means', { label: t(row.label) })}
                      title={t(row.label)}
                      glyph="i"
                    >
                      {row.info}
                    </InfoTip>
                  )}
                </dt>
                <dd className="acct-sheet__balance-value">{row.value}</dd>
              </div>
            ))}
          </dl>
        )}

        <div className="acct-sheet__group">
          {/* Expands in place rather than opening a second panel: the list is
              three lines, and a phone has nowhere to put a panel beside one. */}
          <button
            type="button"
            className={`acct-sheet__row${notesOpen ? ' is-open' : ''}`}
            role="menuitem"
            aria-expanded={notesOpen}
            onClick={() => setNotesOpen((v) => !v)}
          >
            <span className="acct-sheet__row-label">{t('Notifications')}</span>
            {hasUnread && <span className="acct-sheet__dot" aria-label={t('Unread')} />}
            <span className="icon acct-sheet__chev" style={icon('chevron-down-md')} />
          </button>

          {notesOpen && (
            <div className="acct-sheet__notes">
              {notifications.map((note) => (
                <div className={`acct-sheet__note is-${note.type}`} key={note.id}>
                  <span className="acct-sheet__note-title">{note.title}</span>
                  <span className="acct-sheet__note-text">{note.message}</span>
                  <span className="acct-sheet__note-time">{note.time}</span>
                </div>
              ))}

              {notifications.length === 0 && (
                <p className="acct-sheet__note-empty">{t('Nothing new right now.')}</p>
              )}

              <div className="acct-sheet__note-actions">
                <NavLink to="/notifications" className="acct-sheet__mark" onClick={onClose}>
                  {t('See all')}
                </NavLink>

                {hasUnread && notifications.length > 0 && (
                  <button type="button" className="acct-sheet__mark" onClick={onMarkRead}>
                    {t('Mark all as read')}
                  </button>
                )}
              </div>
            </div>
          )}

          <NavLink to="/profile" className="acct-sheet__row" role="menuitem" onClick={onClose}>
            <span className="acct-sheet__row-label">{t('Profile')}</span>
          </NavLink>

          {/* The sheet, not the page: `/p/:accountId` only resolves once the
              account is published, and that switch lives in the share sheet. */}
          <button
            type="button"
            className="acct-sheet__row"
            role="menuitem"
            onClick={onPublicPage}
          >
            <span className="acct-sheet__row-label">{t('Public Page')}</span>
          </button>
        </div>

        <div className="acct-sheet__group">
          <span className="acct-sheet__group-label">{t('Get started')}</span>
          {GET_STARTED.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) => `acct-sheet__row${isActive ? ' is-active' : ''}`}
              role="menuitem"
              onClick={onClose}
            >
              <span className="icon acct-sheet__row-icon" style={icon(item.icon)} />
              <span className="acct-sheet__row-label">{t(item.label)}</span>
            </NavLink>
          ))}
        </div>

        <button
          type="button"
          className="acct-sheet__row acct-sheet__row--danger"
          role="menuitem"
          onClick={onSignOut}
        >
          <span className="acct-sheet__row-label">{t('Log out')}</span>
        </button>
      </div>
    </>
  );
}
